import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import UsuarioAuth from '../../components/classes/UsuarioAuth';
import Autenticar from './Autenticar';
import { hasInCache } from '../../helper/cacheHelper';
import RecaptchaComponent, { RecaptchaRef } from '../../components/common/RecaptchaComponent';

import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const DO_NOT_LOGIN = false;
const cacheKey = 'AutUser';
const rememberedEmailKey = 'rememberedEmail';

const schema = z.object({
  email: z.coerce
    .string()
    .trim()
    .min(1, { message: 'Email é obrigatório' })
    .email({ message: 'Formato de email inválido' }),

  password: z.coerce
    .string()
    .trim()
    .min(1, { message: 'Senha é obrigatória' }),

  remember: z.boolean().optional().default(false),

  recaptchaToken: z.coerce
    .string()
    .trim()
    .min(1, { message: 'Confirme que você não é um robô' }),
});

type LoginForm = z.infer<typeof schema>;

function Copyright(props: any) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="/">
        Copacker
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

export default function SignIn() {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const recaptchaRef = React.useRef<RecaptchaRef>(null);

  const rememberedEmail = React.useMemo(() => localStorage.getItem(rememberedEmailKey) || '', []);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<LoginForm>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      email: rememberedEmail ?? '',
      password: '',
      remember: !!rememberedEmail,
      recaptchaToken: '',
    },
  });

  React.useEffect(() => {
    if (hasInCache(cacheKey)) window.location.href = '/';
    const input = document.getElementById('email');
    input?.focus();
  }, []);

  const CallAzure = () => {
    const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
    const runLocalhost = false;
    const localhostUrl = runLocalhost ? '&redirect_uri=http://localhost:3000/Usuario/AzureSignOn' : '';
    window.location.href = `https://login.microsoftonline.com/27951710-ebb9-4efb-a4a5-48e460822d4b/oauth2/authorize?response_type=id_token&client_id=${clientId}&state=8542f20c-246a-4033-832f-ff05eb694f95&x-client-SKU=Js&x-client-Ver=1.0.17&nonce=79e18681-18b7-4cfd-bf69-813166cbe8da${localhostUrl}`;
  };

  const onSubmit = async (data: LoginForm) => {
    if (DO_NOT_LOGIN) return;

    setErrorMsg(null);
    setIsLoading(true);

    try {
      const email = (data.email ?? '').trim();
      const password = (data.password ?? '').trim();
      const recaptchaToken = (data.recaptchaToken ?? '').trim();

      if (data.remember) {
        localStorage.setItem(rememberedEmailKey, email);
      } else {
        localStorage.removeItem(rememberedEmailKey);
      }

      const userBefore: UsuarioAuth & { recaptchaToken?: string } = {
        ...new UsuarioAuth(),
        autenticado: false,
        email,
        passwordHash: password,
        token: '',
        ultimoAcesso: new Date(),
        userName: '',
        recaptchaToken,
      };

      localStorage.removeItem(cacheKey);

      await Autenticar(userBefore);
      window.location.replace('/');
    } catch (error) {
      setIsLoading(false);
      
      const title = t('pages.login.text.loginFailed') || 'Falha no login';
      const message = t('pages.login.text.checkUserPassAndTryAgain') || 'Verifique suas credenciais e tente novamente.';
      setErrorMsg(`${title}. ${message}`);
      
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setValue('recaptchaToken', '');
      }
    }
  };

  const recaptchaToken = watch('recaptchaToken');

  return (
    <Grid container component="main" sx={{ minHeight: '100vh' }}>
      {/* Painel esquerdo com imagem */}
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          backgroundImage: 'url("/background-login.png")',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: { xs: 'none', md: 'block' },
        }}
      />

      {/* Painel direito com formulário */}
      <Grid item xs={12} md={6} display="flex" alignItems="center" justifyContent="center" bgcolor={'white'}>
          <Stack spacing={2}>
            <Box textAlign="center" sx={{ mb: 2 }}>
              <Box component="img" src="/brfs.png" alt="Logo" sx={{ height: 48, mb: 1 }} />
              <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                {t('home.welcome') || 'Bem-vindo(a)'}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {t('pages.login.text.subtitle') || 'Acesse com sua conta corporativa.'}
              </Typography>
            </Box>

            {errorMsg && (
              <Alert severity="error" role="alert" aria-live="assertive">
                {errorMsg}
              </Alert>
            )}

            <Stack component="form" noValidate spacing={2} onSubmit={handleSubmit(onSubmit)}>
              {/* Email */}
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    id="email"
                    label={t('pages.login.fields.email') || 'Email'}
                    type="email"
                    autoComplete="email"
                    autoFocus
                    fullWidth
                    size="medium"
                    disabled={isLoading}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    aria-invalid={!!errors.email}
                  />
                )}
              />

              {/* Senha */}
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    id="password"
                    label={t('pages.login.fields.password') || 'Senha'}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    fullWidth
                    size="medium"
                    disabled={isLoading}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    aria-invalid={!!errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* reCAPTCHA */}
              <Controller
                name="recaptchaToken"
                control={control}
                render={({ field }) => (
                  <RecaptchaComponent
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                    onChange={(token) => field.onChange(token || '')}
                    onExpired={() => field.onChange('')}
                    onError={() => field.onChange('')}
                    disabled={isLoading}
                    errorMessage={errors.recaptchaToken?.message}
                    data-testid="login-recaptcha"
                  />
                )}
              />

              {/* Lembrar-me */}
              <Controller
                name="remember"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={!!field.value} color="primary" disabled={isLoading} />}
                    label={t('pages.login.text.rememberMe') || 'Lembrar-me'}
                  />
                )}
              />

              {/* Botão Entrar */}
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isLoading || !isValid || !recaptchaToken}
                  startIcon={!isLoading ? <LoginIcon /> : undefined}
                  aria-label="Entrar"
                  sx={{ 
                    minHeight: 48, // Mantém altura consistente
                    position: 'relative'
                  }}
                >
                  {isLoading ? (
                    <CircularProgress 
                      size={22} 
                      color="inherit" 
                      sx={{ 
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)'
                      }}
                    />
                  ) : (
                    t('pages.login.text.login') || 'Entrar'
                  )}
                </Button>
              </Box>

              <Divider flexItem>{t('pages.login.text.or') || 'ou'}</Divider>

              {/* Azure AD */}
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={CallAzure}
                disabled={isLoading}
                aria-label="Entrar com Microsoft"
              >
                {t('pages.login.text.microsoftAuth') || 'Entrar com Microsoft'}
              </Button>
            </Stack>

            <Copyright sx={{ mt: 1 }} />
          </Stack>
      </Grid>
    </Grid>
  );
}
